import { ImportWizardState, SelectedFile } from '../components/types';
import { ImportMappingRequest } from '@/services/importMappingsApi';

/**
 * Extract CSV file ID from file identifier
 * Handles both uploaded files (filename) and CSV importer files (csv-{id})
 * Returns integer for CSV importer files, string for others
 */
export function extractCsvFileId(
  fileId: string,
  selectedFiles: SelectedFile[]
): number | string {
  // Check if this is a csv-upload file (format: csv-{id})
  if (fileId.startsWith('csv-')) {
    const csvFileId = fileId.replace('csv-', '');
    // Convert to number as backend expects integer for csv_file_id
    const numericId = parseInt(csvFileId, 10);
    return isNaN(numericId) ? csvFileId : numericId;
  }

  // For uploaded files, find the corresponding CSV file ID
  const selectedFile = selectedFiles.find(file => {
    if (file.source === 'uploaded' && file.file) {
      return file.file.name === fileId;
    }
    return false;
  });

  if (selectedFile?.source === 'csv-importer' && selectedFile.csvFile) {
    return selectedFile.csvFile.id;
  }

  // Fallback: if we can't find the file, return the fileId as-is
  // This might happen during development or if there's a data inconsistency
  return fileId;
}

/**
 * Extract CSV file version ID from file identifier
 * Handles both uploaded files (filename) and CSV importer files (csv-{id})
 * Returns version ID if available, undefined otherwise
 */
export function extractCsvFileVersionId(
  fileId: string,
  selectedFiles: SelectedFile[]
): number | undefined {
  // Check if this is a csv-upload file (format: csv-{id})
  if (fileId.startsWith('csv-')) {
    const csvFileId = fileId.replace('csv-', '');
    const numericId = parseInt(csvFileId, 10);
    
    if (!isNaN(numericId)) {
      // Find the corresponding CSV file to get version ID
      const selectedFile = selectedFiles.find(file => {
        if (file.source === 'csv-importer' && file.csvFile) {
          return file.csvFile.id === numericId;
        }
        return false;
      });
      
      // Prefer version_for_import.id (version approved for import)
      if (selectedFile?.csvFile?.version_for_import?.id) {
        return selectedFile.csvFile.version_for_import.id;
      }
      
      // Fallback to last_version_id
      if (selectedFile?.csvFile?.last_version_id) {
        return selectedFile.csvFile.last_version_id;
      }
    }
  }

  // For uploaded files, find the corresponding CSV file version ID
  // Note: uploaded files might not have csvFile yet, so version might be unavailable
  const selectedFile = selectedFiles.find(file => {
    if (file.source === 'uploaded' && file.file) {
      return file.file.name === fileId;
    }
    // Also check csv-importer files that might match by name
    if (file.source === 'csv-importer' && file.csvFile) {
      return file.csvFile.filename === fileId;
    }
    return false;
  });

  // Prefer version_for_import.id (version approved for import)
  if (selectedFile?.csvFile?.version_for_import?.id) {
    return selectedFile.csvFile.version_for_import.id;
  }

  // Fallback to last_version_id
  if (selectedFile?.csvFile?.last_version_id) {
    return selectedFile.csvFile.last_version_id;
  }

  // Return undefined if version is not available
  return undefined;
}

/**
 * Extract form ID from FileToFormMapping
 * Converts number to string as required by API
 */
export function extractFormId(formId: number): string {
  return formId.toString();
}

/**
 * Transform wizard state mappings to API format
 */
export function transformWizardStateToApiRequest(
  wizardState: ImportWizardState
): ImportMappingRequest {
  const {
    fileToFormMappings,
    columnMappings,
    subformRelationships,
    selectedFiles,
  } = wizardState;

  // Transform file-to-form mappings
  const fileToFormMappingsApi = fileToFormMappings.map(mapping => {
    const csvFileId = extractCsvFileId(mapping.fileId, selectedFiles);
    const csvFileVersionId = extractCsvFileVersionId(mapping.fileId, selectedFiles);
    
    return {
      csv_file_id: csvFileId,
      ...(csvFileVersionId && { csv_file_version_id: csvFileVersionId }),
      form_id: extractFormId(mapping.formId),
      match_type: mapping.matchType,
      ...(mapping.nested_path && { nested_path: mapping.nested_path }),
    };
  });

  // Transform column mappings
  const columnMappingsApi = columnMappings.map(mapping => {
    // Find the corresponding form_id for this file
    const fileToFormMapping = fileToFormMappings.find(
      ftfm => ftfm.fileId === mapping.fileId
    );

    if (!fileToFormMapping) {
      throw new Error(
        `No form mapping found for file "${mapping.fileId}" when mapping column "${mapping.columnName}"`
      );
    }

    const csvFileId = extractCsvFileId(mapping.fileId, selectedFiles);
    const csvFileVersionId = extractCsvFileVersionId(mapping.fileId, selectedFiles);
    
    return {
      csv_file_id: csvFileId,
      ...(csvFileVersionId && { csv_file_version_id: csvFileVersionId }),
      column_name: mapping.columnName,
      mapping_type:
        mapping.mappingType === 'unclassified'
          ? 'ignored'
          : mapping.mappingType,
      form_id: extractFormId(fileToFormMapping.formId),
      // TODO: Slavik: REMOVE THIS WHEN BACKEND IS UPDATED
      study_form_id: fileToFormMapping.formId,
      ...(mapping.metadataType && {
        metadata_type: mapping.metadataType.type,
      }),
      ...(mapping.targetField && {
        target_field: mapping.targetField,
      }),
    };
  });

  // Transform subform relationships
  const subformRelationshipsApi = subformRelationships.map(relationship => ({
    subform_id: relationship.subformId,
    parent_form_id: relationship.parentFormId,
    is_valid: relationship.isValid,
  }));

  return {
    file_to_form_mappings: fileToFormMappingsApi,
    column_mappings: columnMappingsApi,
    subform_relationships: subformRelationshipsApi,
  };
}

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  type:
    | 'invalid_file_id'
    | 'missing_required_mapping'
    | 'invalid_mapping_type'
    | 'orphaned_mapping';
  message: string;
  details?: string;
  fileId?: string;
  columnName?: string;
}

export interface ValidationWarning {
  type: 'unmapped_columns' | 'potential_duplicates';
  message: string;
  details?: string;
  fileId?: string;
  columnName?: string;
}

/**
 * Validate that all mappings have valid CSV file IDs and other constraints
 */
export function validateMappingData(
  wizardState: ImportWizardState
): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const { fileToFormMappings, columnMappings, selectedFiles } = wizardState;

  // Track processed file IDs for validation
  const processedFileIds = new Set<string>();

  // Check file-to-form mappings
  fileToFormMappings.forEach(mapping => {
    const csvFileId = extractCsvFileId(mapping.fileId, selectedFiles);
    if (!csvFileId || csvFileId === mapping.fileId) {
      // If csvFileId is the same as fileId, it means extraction failed
      errors.push({
        type: 'invalid_file_id',
        message: `Unable to resolve file ID for mapping`,
        details: `File identifier "${mapping.fileId}" could not be converted to a valid CSV file ID`,
        fileId: mapping.fileId,
      });
    } else {
      processedFileIds.add(mapping.fileId);
    }
  });

  // Check column mappings
  columnMappings.forEach(mapping => {
    const csvFileId = extractCsvFileId(mapping.fileId, selectedFiles);
    if (!csvFileId || csvFileId === mapping.fileId) {
      // If csvFileId is the same as fileId, it means extraction failed
      errors.push({
        type: 'invalid_file_id',
        message: `Unable to resolve file ID for column mapping`,
        details: `Column "${mapping.columnName}" in file "${mapping.fileId}" has invalid file reference`,
        fileId: mapping.fileId,
        columnName: mapping.columnName,
      });
    } else if (!processedFileIds.has(mapping.fileId)) {
      // Column mapping exists for file that doesn't have file-to-form mapping
      errors.push({
        type: 'orphaned_mapping',
        message: `Column mapping exists for unmapped file`,
        details: `Column "${mapping.columnName}" is mapped but file "${mapping.fileId}" has no form mapping`,
        fileId: mapping.fileId,
        columnName: mapping.columnName,
      });
    }
  });

  // Check for required mappings
  selectedFiles.forEach(file => {
    const fileId =
      file.source === 'uploaded' ? file.file!.name : `csv-${file.csvFile!.id}`;

    // Check if file has a form mapping
    const hasFormMapping = fileToFormMappings.some(m => m.fileId === fileId);
    if (!hasFormMapping) {
      errors.push({
        type: 'missing_required_mapping',
        message: `File missing form mapping`,
        details: `File "${fileId}" must be mapped to a form before import`,
        fileId: fileId,
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}
